import { Outlet, useLocation } from '@tanstack/react-router';
import { ProgressCircle } from '@yembo/yemblocks-core';
import { Ref, Suspense, useContext } from 'react';
import { Collapsible, CollapsibleNotification } from '../_shared/CollapsibleNotification';
import { AddContactProvider } from '../_shared/Context/AddContact/provider';
import { ChatBoxProvider } from '../_shared/Context/Chat/provider';
import { ExportRequestContextProvider } from '../_shared/Context/ExportRequest/provider';
import { InboxTotalProvider } from '../_shared/Context/InboxTotal/provider';
import { ItemImportProvider } from '../_shared/Context/ItemImport/provider';
import { PreventUpdatingProvider } from '../_shared/Context/PreventUpdatingContext/provider';
import { ScrollToImageProvider } from '../_shared/Context/ScrollToImage/provider';
import { GenerateJobsCSVNotification } from '../_shared/GenerateJobsCSVNotification';
import { ItemImportProgressToast } from '../_shared/ItemImportProgressToast';
import { LoadingAnimation } from '../_shared/LoadingAnimation';
import { OfflineNotification } from '../_sw/OfflineNotification';
import { OnlineStatusContext } from '../_sw/OnlineStatusProvider/context';
import { Sidebar } from '../Sidebar';

type Props = {
  sideBarRef: Ref<HTMLDivElement>;
};

export const Wrapper = ({ sideBarRef }: Props) => {
  const { isOnline, requestQueueLength, requestQueueLengthStart } = useContext(OnlineStatusContext);
  const location = useLocation();

  const basePath = location.pathname.split('/')[1];

  return (
    <div id='protected-views-wrapper'>
      <PreventUpdatingProvider>
        <ChatBoxProvider>
          <AddContactProvider>
            <InboxTotalProvider>
              <ScrollToImageProvider>
                <ExportRequestContextProvider>
                  <ItemImportProvider>
                    <Sidebar ref={sideBarRef} />
                    <div className='app-screen-container'>
                      <Suspense fallback={<LoadingAnimation />} key={basePath}>
                        <Outlet />
                      </Suspense>
                    </div>
                    {requestQueueLength !== 0 && isOnline && (
                      <CollapsibleNotification className='back-online-notification'>
                        <ProgressCircle
                          value={Math.round(
                            ((requestQueueLengthStart - requestQueueLength) / requestQueueLengthStart) * 100
                          )}
                        />
                        <Collapsible>
                          <div className='bold back-online-subheader'>You are back online 🎉</div>
                          <div className='back-online-message'>
                            The changes made while you were offline are uploading now
                          </div>
                        </Collapsible>
                      </CollapsibleNotification>
                    )}

                    <OfflineNotification />
                    <GenerateJobsCSVNotification />
                    <ItemImportProgressToast />
                  </ItemImportProvider>
                </ExportRequestContextProvider>
              </ScrollToImageProvider>
            </InboxTotalProvider>
          </AddContactProvider>
        </ChatBoxProvider>
      </PreventUpdatingProvider>
    </div>
  );
};
